import React, { useEffect, useState } from "react";
import { api } from "../../lib/api";
import { Card } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { EmptyState, SkeletonTabela } from "../../components/EmptyState";
import { FileText, Download, AlertTriangle, Loader2 } from "lucide-react";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STATUS_BADGE = {
  ativo: { bg: "bg-emerald-100 text-emerald-700", label: "Ativo" },
  encerrado: { bg: "bg-zinc-100 text-zinc-700", label: "Encerrado" },
  vencido: { bg: "bg-red-100 text-red-700", label: "Vencido" },
  rascunho: { bg: "bg-amber-100 text-amber-700", label: "Rascunho" },
};

const fmt = (v) => new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(v || 0);

export default function ClienteContratos() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFiltro, setStatusFiltro] = useState("todos");
  const [downloading, setDownloading] = useState(null);

  const carregar = () => {
    setLoading(true);
    const params = statusFiltro !== "todos" ? { status: statusFiltro } : {};
    api.get("/portal/contratos", { params })
      .then((r) => setItems(r.data.items || []))
      .catch(() => setItems([]))
      .finally(() => setLoading(false));
  };

  useEffect(carregar, [statusFiltro]);

  const baixarPDF = async (c) => {
    setDownloading(c.id);
    try {
      const token = localStorage.getItem("lab_token") || "";
      const resp = await fetch(`${BACKEND_URL}/api/portal/contratos/${c.id}/pdf`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!resp.ok) {
        if (resp.status === 403) toast.error("Acesso negado a este contrato");
        else if (resp.status === 404) toast.error("PDF ainda não gerado para este contrato");
        else toast.error("Falha ao baixar PDF");
        return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `contrato-${c.id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch {
      toast.error("Falha ao baixar PDF");
    } finally {
      setDownloading(null);
    }
  };

  return (
    <div className="space-y-4 max-w-4xl mx-auto" data-testid="page-cliente-contratos">
      <div>
        <h1 className="text-2xl sm:text-3xl font-display font-bold text-zinc-900">Meus contratos</h1>
        <p className="text-sm text-zinc-500 mt-1">
          {items.length} contrato{items.length !== 1 ? "s" : ""}
        </p>
      </div>

      <div className="flex gap-2 flex-wrap">
        {["todos", "ativo", "encerrado", "vencido"].map((s) => (
          <Button
            key={s}
            size="sm"
            variant={statusFiltro === s ? "default" : "outline"}
            onClick={() => setStatusFiltro(s)}
            className={statusFiltro === s ? "bg-orange-600 hover:bg-orange-700 text-white" : ""}
            data-testid={`filtro-status-${s}`}
          >
            {s.charAt(0).toUpperCase() + s.slice(1)}
          </Button>
        ))}
      </div>

      {loading ? (
        <Card><SkeletonTabela linhas={5} colunas={4} /></Card>
      ) : items.length === 0 ? (
        <Card data-testid="cliente-contratos-empty">
          <EmptyState
            icone={FileText}
            titulo="Você ainda não possui contratos ativos."
            descricao="Quando seu primeiro contrato for assinado, ele aparecerá aqui automaticamente."
          />
        </Card>
      ) : (
        <div className="space-y-3">
          {items.map((c) => {
            const sb = STATUS_BADGE[c.status] || STATUS_BADGE.encerrado;
            const venc = c.vencimento ? new Date(c.vencimento) : null;
            const dias = c.dias_para_vencimento;
            const alerta = c.alerta;
            return (
              <Card
                key={c.id}
                className={`p-4 ${alerta === "vencido" ? "bg-red-50 border-red-200" : alerta === "vence_em_30" ? "bg-amber-50 border-amber-200" : ""}`}
                data-testid={`contrato-${c.id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-semibold text-zinc-900">
                      Contrato #{c.numero || c.id.slice(-6).toUpperCase()}
                    </h3>
                    <div className="text-xs text-zinc-500 mt-1 space-y-0.5">
                      {c.inicio && (
                        <div>Início: <strong className="text-zinc-700">{new Date(c.inicio).toLocaleDateString("pt-BR")}</strong></div>
                      )}
                      {venc && (
                        <div>
                          Vencimento: <strong className="text-zinc-700">{venc.toLocaleDateString("pt-BR")}</strong>
                          {dias !== undefined && dias !== null && (
                            <span className={`ml-2 text-[11px] ${dias < 0 ? "text-red-600 font-semibold" : dias <= 30 ? "text-amber-700 font-medium" : "text-zinc-400"}`}>
                              {dias < 0 ? `(venceu há ${-dias} dia${-dias !== 1 ? "s" : ""})` : `(em ${dias} dias)`}
                            </span>
                          )}
                        </div>
                      )}
                      {c.valor_mensal != null && (
                        <div>Valor mensal: <strong className="text-orange-600">{fmt(c.valor_mensal)}</strong></div>
                      )}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-2">
                    <Badge className={sb.bg}>{sb.label}</Badge>
                    {alerta === "vencido" && <AlertTriangle className="w-4 h-4 text-red-600" />}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => baixarPDF(c)}
                      disabled={downloading === c.id || !c.pdf_url}
                      title={!c.pdf_url ? "PDF ainda não gerado" : "Baixar contrato"}
                      data-testid={`pdf-${c.id}`}
                    >
                      {downloading === c.id ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Download className="w-3.5 h-3.5 mr-1" />}
                      PDF
                    </Button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
